import React, { useEffect, useMemo } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Header from "../Header/Header";
import SingleUserCard from "../UserCard";
import { fetchUsers } from "../../redux/maindashboard/dashboardSlice";

import {
  FaArrowLeft,
  FaStethoscope,
  FaUsers,
  FaExclamationCircle,
  FaMapMarkerAlt,
  FaUserMd,
  FaShieldAlt,
  FaCheckCircle,
  FaCalendarCheck,
  FaPhoneAlt,
} from "react-icons/fa";

/* =========================================================
   LOADING CARD
   ========================================================= */

const LoadingCard = () => {
  return (
    <div className="w-full min-w-0 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      {/* Image Skeleton */}
      <div className="relative h-40 w-full animate-pulse bg-gray-200 sm:h-44 md:h-48">
        <div className="absolute left-3 top-3 h-8 w-8 rounded-full bg-gray-300" />
      </div>

      {/* Content Skeleton */}
      <div className="space-y-3 p-4">
        <div className="h-5 w-3/4 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-full animate-pulse rounded bg-gray-200" />

        {/* Action Buttons Skeleton */}
        <div className="grid grid-cols-2 gap-2">
          <div className="h-10 animate-pulse rounded-xl bg-gray-200" />
          <div className="h-10 animate-pulse rounded-xl bg-gray-200" />
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   LOADING GRID
   ========================================================= */

const LoadingGrid = () => {
  return (
    <div
      className="
        grid
        w-full
        grid-cols-1
        gap-4
        sm:grid-cols-2
        md:gap-5
        lg:grid-cols-3
        xl:grid-cols-4
        2xl:grid-cols-5
      "
    >
      {Array.from({ length: 10 }).map((_, index) => (
        <LoadingCard key={index} />
      ))}
    </div>
  );
};

/* =========================================================
   EMPTY STATE
   ========================================================= */

const EmptyState = ({ categoryName }) => {
  return (
    <div className="flex min-h-[42vh] w-full items-center justify-center px-3 py-8 sm:px-5">
      <div
        className="
          w-full
          max-w-lg
          rounded-3xl
          border
          border-gray-100
          bg-white
          px-5
          py-8
          text-center
          shadow-sm
          sm:px-8
          sm:py-10
        "
      >
        {/* Empty Icon */}
        <div
          className="
            mx-auto
            flex
            h-14
            w-14
            items-center
            justify-center
            rounded-2xl
            bg-gradient-to-br
            from-cyan-50
            to-blue-100
            text-blue-600
            shadow-sm
            ring-1
            ring-blue-100
            sm:h-16
            sm:w-16
          "
        >
          <FaUserMd className="text-xl sm:text-2xl" />
        </div>

        <h2 className="mt-4 text-lg font-bold text-gray-900 sm:text-xl">
          No professionals found
        </h2>

        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-500">
          We couldn't find healthcare professionals for{" "}
          <span className="font-semibold text-gray-700">
            {categoryName}
          </span>{" "}
          in your selected location.
        </p>

        {/* Back Button */}
        <Link
          to="/Home"
          className="
            group
            mt-5
            inline-flex
            min-h-[44px]
            items-center
            justify-center
            gap-2
            rounded-xl
            bg-gradient-to-r
            from-cyan-500
            via-blue-600
            to-indigo-600
            px-5
            text-sm
            font-semibold
            text-white
            shadow-md
            transition-all
            duration-200
            hover:-translate-y-0.5
            hover:shadow-lg
            active:translate-y-0
            sm:mt-6
          "
        >
          <FaArrowLeft className="transition-transform duration-200 group-hover:-translate-x-0.5" />
          Back to Search
        </Link>
      </div>
    </div>
  );
};

/* =========================================================
   ERROR STATE
   ========================================================= */

const ErrorState = ({ error }) => {
  return (
    <div className="flex min-h-[42vh] w-full items-center justify-center px-3 py-8 sm:px-5">
      <div
        className="
          w-full
          max-w-lg
          rounded-3xl
          border
          border-red-100
          bg-red-50
          p-6
          text-center
          sm:p-8
        "
      >
        {/* Error Icon */}
        <div
          className="
            mx-auto
            flex
            h-14
            w-14
            items-center
            justify-center
            rounded-full
            bg-red-100
            text-red-500
            sm:h-16
            sm:w-16
          "
        >
          <FaExclamationCircle className="text-2xl sm:text-3xl" />
        </div>

        <h2 className="mt-4 text-lg font-bold text-red-700 sm:text-xl">
          Unable to load professionals
        </h2>

        <p className="mt-2 text-sm leading-6 text-red-600">
          Something went wrong while loading healthcare professionals.
          Please try again or return to the search page.
        </p>

        {error && (
          <p className="mt-3 break-words rounded-xl bg-white/70 px-3 py-2 text-xs text-red-500">
            {typeof error === "string"
              ? error
              : "Unable to fetch professionals."}
          </p>
        )}

        {/* Back Button */}
        <Link
          to="/"
          className="
            mt-5
            inline-flex
            min-h-[44px]
            items-center
            justify-center
            gap-2
            rounded-xl
            bg-red-600
            px-5
            text-sm
            font-semibold
            text-white
            shadow-sm
            transition-all
            duration-200
            hover:-translate-y-0.5
            hover:bg-red-700
            hover:shadow-md
          "
        >
          <FaArrowLeft />
          Back to Search
        </Link>
      </div>
    </div>
  );
};

/* =========================================================
   FEATURE ITEM
   ========================================================= */

const FeatureItem = ({
  icon,
  title,
  description,
  iconBg,
  iconColor,
}) => {
  return (
    <div
      className="
        group
        flex
        min-w-0
        items-center
        gap-3
        rounded-2xl
        border
        border-gray-100
        bg-white
        px-3
        py-3
        shadow-sm
        transition-all
        duration-200
        hover:-translate-y-0.5
        hover:shadow-md
        sm:px-4
        sm:py-3.5
      "
    >
      {/* Feature Icon */}
      <div
        className={`
          flex
          h-10
          w-10
          shrink-0
          items-center
          justify-center
          rounded-xl
          ${iconBg}
          ${iconColor}
          transition-transform
          duration-200
          group-hover:scale-105
        `}
      >
        {icon}
      </div>

      {/* Feature Text */}
      <div className="min-w-0">
        <p className="truncate text-xs font-bold text-gray-800 sm:text-sm">
          {title}
        </p>

        <p className="mt-0.5 truncate text-[10px] text-gray-400 sm:text-xs">
          {description}
        </p>
      </div>
    </div>
  );
};

/* =========================================================
   TRUST / ACTION STRIP
   ========================================================= */

const TrustStrip = () => {
  return (
    <div
      className="
        mb-5
        grid
        grid-cols-1
        gap-2
        sm:grid-cols-2
        sm:gap-3
        lg:grid-cols-4
        lg:mb-6
      "
    >
      {/* Nearby */}
      <FeatureItem
        icon={<FaMapMarkerAlt className="text-sm" />}
        title="Find Nearby"
        description="Professionals near your location"
        iconBg="bg-blue-50"
        iconColor="text-blue-600"
      />

      {/* Appointment */}
      <FeatureItem
        icon={<FaCalendarCheck className="text-sm" />}
        title="Book Appointment"
        description="Schedule your healthcare visit"
        iconBg="bg-cyan-50"
        iconColor="text-cyan-600"
      />

      {/* Contact */}
      <FeatureItem
        icon={<FaPhoneAlt className="text-sm" />}
        title="Contact Professionals"
        description="Connect with healthcare providers"
        iconBg="bg-green-50"
        iconColor="text-green-600"
      />

      {/* Trust */}
      <FeatureItem
        icon={<FaShieldAlt className="text-sm" />}
        title="Trusted Healthcare"
        description="Search with confidence"
        iconBg="bg-indigo-50"
        iconColor="text-indigo-600"
      />
    </div>
  );
};

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

const UserCardListPage = () => {
  const { subcategory } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  /* =======================================================
     CATEGORY
     ======================================================= */

  const categoryName = useMemo(() => {
    if (!subcategory) {
      return "All Professionals";
    }

    try {
      return decodeURIComponent(subcategory);
    } catch {
      return subcategory;
    }
  }, [subcategory]);

  /* =======================================================
     NAVIGATION USERS
     ======================================================= */

  const navigationUsers = useMemo(() => {
    const users = location.state?.users;

    return Array.isArray(users) ? users : [];
  }, [location.state]);

  /* =======================================================
     REDUX
     ======================================================= */

  const postcode = useSelector(
    (state) => state.businessUserSearch?.postcode || ""
  );

  const reduxUsers = useSelector(
    (state) => state.users?.users || []
  );

  const loading = useSelector(
    (state) => state.users?.loading || false
  );

  const reduxError = useSelector(
    (state) => state.users?.error || null
  );

  /* =======================================================
     FETCH USERS
     ======================================================= */

  useEffect(() => {
    if (!subcategory || !postcode) {
      return;
    }

    dispatch(
      fetchUsers({
        postcode,
        subcategory: categoryName,
      })
    );
  }, [
    dispatch,
    postcode,
    subcategory,
    categoryName,
  ]);

  /* =======================================================
     USERS
     ======================================================= */

  const users = useMemo(() => {
    if (navigationUsers.length > 0) {
      return navigationUsers;
    }

    return Array.isArray(reduxUsers)
      ? reduxUsers
      : [];
  }, [navigationUsers, reduxUsers]);

  const totalUsers = users.length;

  /* =======================================================
     RETURN
     ======================================================= */

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-slate-50">
      {/* =====================================================
          HEADER
          ===================================================== */}

      <Header />

      {/* =====================================================
          MAIN
          ===================================================== */}

      <main
        className="
          mx-auto
          w-full
          max-w-[1800px]
          px-3
          py-4
          sm:px-5
          sm:py-5
          md:px-6
          lg:px-8
          xl:px-10
          2xl:px-12
        "
      >
        {/* ===================================================
            BACK BUTTON
            =================================================== */}

        <div className="mb-4 sm:mb-5">
          <Link
            to="/"
            className="
              group
              inline-flex
              min-h-[40px]
              items-center
              gap-2
              rounded-xl
              border
              border-transparent
              px-2
              text-xs
              font-semibold
              text-blue-600
              transition-all
              duration-200
              hover:border-blue-100
              hover:bg-blue-50
              hover:text-blue-700
              sm:px-3
              sm:text-sm
            "
          >
            <span
              className="
                flex
                h-7
                w-7
                items-center
                justify-center
                rounded-full
                bg-blue-50
                transition-transform
                duration-200
                group-hover:-translate-x-0.5
              "
            >
              <FaArrowLeft className="text-xs" />
            </span>

            Back to Search
          </Link>
        </div>

        {/* ===================================================
            HERO
            =================================================== */}

        <section
          className="
            relative
            mb-5
            w-full
            overflow-hidden
            rounded-2xl
            bg-gradient-to-r
            from-cyan-600
            via-blue-600
            to-indigo-700
            px-4
            py-5
            text-white
            shadow-lg
            sm:mb-6
            sm:rounded-3xl
            sm:px-6
            sm:py-6
            md:px-7
            lg:px-8
            lg:py-7
          "
        >
          {/* Decorative Circle 1 */}
          <div
            className="
              pointer-events-none
              absolute
              -right-16
              -top-20
              h-40
              w-40
              rounded-full
              border
              border-white/10
              bg-white/10
              sm:h-56
              sm:w-56
              md:h-64
              md:w-64
            "
          />

          {/* Decorative Circle 2 */}
          <div
            className="
              pointer-events-none
              absolute
              -bottom-20
              -left-16
              h-40
              w-40
              rounded-full
              border
              border-white/10
              bg-white/10
              sm:h-56
              sm:w-56
              md:h-64
              md:w-64
            "
          />

          {/* Decorative Circle 3 */}
          <div
            className="
              pointer-events-none
              absolute
              right-[30%]
              top-1/2
              hidden
              h-20
              w-20
              -translate-y-1/2
              rounded-full
              bg-white/5
              md:block
              lg:h-28
              lg:w-28
            "
          />

          {/* Hero Content */}
          <div
            className="
              relative
              z-10
              flex
              flex-col
              gap-5
              sm:gap-6
              md:flex-row
              md:items-center
              md:justify-between
            "
          >
            {/* Category */}
            <div className="flex min-w-0 items-start gap-3 sm:gap-4">
              {/* Category Icon */}
              <div
                className="
                  relative
                  flex
                  h-11
                  w-11
                  shrink-0
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-xl
                  border
                  border-white/20
                  bg-white/15
                  shadow-inner
                  backdrop-blur-md
                  sm:h-14
                  sm:w-14
                  sm:rounded-2xl
                "
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />

                <FaStethoscope className="relative z-10 text-lg sm:text-2xl" />
              </div>

              {/* Category Text */}
              <div className="min-w-0">
                <p
                  className="
                    text-[10px]
                    font-semibold
                    uppercase
                    tracking-wider
                    text-blue-100
                    sm:text-xs
                    md:text-sm
                  "
                >
                  Healthcare Professionals
                </p>

                <h1
                  className="
                    mt-1
                    break-words
                    text-lg
                    font-extrabold
                    leading-tight
                    sm:text-2xl
                    md:text-3xl
                  "
                >
                  {categoryName}
                </h1>

                <p
                  className="
                    mt-1
                    max-w-xl
                    text-xs
                    leading-5
                    text-blue-100
                    sm:text-sm
                  "
                >
                  Find trusted healthcare professionals near you.
                </p>
              </div>
            </div>

            {/* Result Count */}
            <div
              className="
                flex
                w-full
                items-center
                gap-3
                rounded-2xl
                border
                border-white/10
                bg-white/10
                px-3
                py-3
                shadow-inner
                backdrop-blur-md
                sm:w-fit
                sm:min-w-[190px]
                sm:px-4
                sm:py-3.5
              "
            >
              <div
                className="
                  flex
                  h-10
                  w-10
                  shrink-0
                  items-center
                  justify-center
                  rounded-xl
                  bg-white
                  text-blue-600
                  shadow-sm
                  sm:h-11
                  sm:w-11
                "
              >
                <FaUsers className="text-sm sm:text-base" />
              </div>

              <div className="min-w-0">
                <p className="text-xl font-extrabold leading-none sm:text-2xl">
                  {totalUsers}
                </p>

                <p className="mt-1 text-[10px] text-blue-100 sm:text-xs">
                  Professionals found
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===================================================
            LOCATION
            =================================================== */}

        {postcode && (
          <div
            className="
              mb-4
              flex
              min-w-0
              items-center
              gap-3
              rounded-2xl
              border
              border-blue-100
              bg-white
              px-3
              py-3
              text-xs
              text-gray-600
              shadow-sm
              sm:mb-5
              sm:px-4
              sm:py-3.5
              sm:text-sm
            "
          >
            {/* Location Icon */}
            <div
              className="
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center
                rounded-xl
                bg-blue-50
                text-blue-600
              "
            >
              <FaMapMarkerAlt className="text-sm" />
            </div>

            {/* Location Text */}
            <div className="min-w-0">
              <p
                className="
                  text-[10px]
                  font-medium
                  uppercase
                  tracking-wide
                  text-gray-400
                  sm:text-xs
                "
              >
                Search Location
              </p>

              <p className="mt-0.5 truncate text-xs text-gray-600 sm:text-sm">
                Professionals around{" "}
                <strong className="font-bold text-gray-800">
                  {postcode}
                </strong>
              </p>
            </div>
          </div>
        )}

        {/* ===================================================
            FEATURE / ACTION ICONS
            =================================================== */}

        <TrustStrip />

        {/* ===================================================
            RESULT HEADER
            =================================================== */}

        {!loading && !reduxError && totalUsers > 0 && (
          <div
            className="
              mb-4
              flex
              flex-col
              gap-2
              sm:mb-5
              sm:flex-row
              sm:items-center
              sm:justify-between
            "
          >
            {/* Title */}
            <div className="flex items-center gap-2">
              <div
                className="
                  flex
                  h-8
                  w-8
                  items-center
                  justify-center
                  rounded-lg
                  bg-blue-50
                  text-blue-600
                "
              >
                <FaUserMd className="text-sm" />
              </div>

              <div>
                <h2 className="text-sm font-bold text-gray-800 sm:text-base">
                  Available Professionals
                </h2>

                <p className="text-[10px] text-gray-400 sm:text-xs">
                  Choose a professional for your healthcare needs
                </p>
              </div>
            </div>

            {/* Available Count */}
            <div
              className="
                flex
                w-fit
                items-center
                gap-1.5
                rounded-full
                bg-green-50
                px-3
                py-1.5
                text-[10px]
                font-semibold
                text-green-600
                sm:text-xs
              "
            >
              <FaCheckCircle />
              {totalUsers} available
            </div>
          </div>
        )}

        {/* ===================================================
            ERROR / LOADING / EMPTY / USERS
            =================================================== */}

        {reduxError ? (
          <ErrorState error={reduxError} />
        ) : loading ? (
          <LoadingGrid />
        ) : totalUsers === 0 ? (
          <EmptyState categoryName={categoryName} />
        ) : (
          <div
            className="
              grid
              w-full
              grid-cols-1
              gap-4
              sm:grid-cols-2
              md:gap-5
              lg:grid-cols-3
              xl:grid-cols-4
              2xl:grid-cols-5
            "
          >
            {users.map((user, index) => {
              const userId =
                user?._id ||
                user?.id ||
                `${categoryName}-${index}`;

              return (
                <div
                  key={userId}
                  className="
                    min-w-0
                    animate-[fadeIn_0.4s_ease-out]
                    transition-all
                    duration-200
                    hover:-translate-y-1
                    hover:drop-shadow-md
                  "
                  style={{
                    animationDelay: `${Math.min(
                      index * 50,
                      400
                    )}ms`,
                  }}
                >
                  <SingleUserCard user={user} />
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* =====================================================
          FADE-IN ANIMATION
          ===================================================== */}

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(8px);
            }

            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default UserCardListPage;